import React, { useState, useCallback, useRef } from 'react';
import { parserService } from '../services/api';
import { ParsedData } from '../types';
import toast from 'react-hot-toast';
import { ArrowLeft, FileText, Image, FolderOpen, X, Upload } from 'lucide-react';

interface UploadResumeProps {
  onParsed: (data: ParsedData, sessionId: string) => void;
  onBack: () => void;
}

const UploadResume: React.FC<UploadResumeProps> = ({ onParsed, onBack }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const validateAndSetFile = (selectedFile: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    
    const ext = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(ext)) {
      toast.error('Please upload a PDF or image file (JPG, PNG, WEBP)');
      return;
    }
    
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setFile(selectedFile);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadAndParse = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);
    setIsParsing(true);
    setProgress(10);

    try {
      setProgress(30);
      const result = await parserService.uploadAndParse(file);
      setProgress(100);

      if (result && result.success && result.data) {
        toast.success('Resume parsed successfully!');
        onParsed(result.data, '');
      } else {
        const errorMsg = result?.error || 'Failed to parse resume';
        toast.error(String(errorMsg));
      }
    } catch (error: unknown) {
      console.error('Upload error:', error);
      const err = error as Error;
      const status = (error as { response?: { status?: number } }).response?.status;
      let errorMessage = 'Failed to upload and parse resume';

      if (status === 429 || status === 503) {
        errorMessage = 'The AI parsing service is temporarily busy (quota exceeded). Please try again in a few minutes.';
      } else if (status === 500) {
        errorMessage = 'Something went wrong while processing your resume. Please try again.';
      } else if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } }; message?: string };
        errorMessage = axiosError.response?.data?.detail || axiosError.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message || errorMessage;
      }

      toast.error(String(errorMessage));
    } finally {
      setIsUploading(false);
      setIsParsing(false);
    }
  };

  return (
    <div className="max-w-[600px] mx-auto p-8">
      <div className="text-center mb-8">
        <h2 className="text-[1.75rem] font-semibold text-foreground mb-2 font-display">Upload Your Resume</h2>
        <p className="text-secondary-foreground text-sm">Upload a PDF or image of your resume to automatically extract your information</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div
        className={`border-2 rounded-card p-12 text-center transition-all duration-300 bg-subtle ${
          file
            ? 'border-solid border-emerald-500 bg-[rgba(16,185,129,0.05)]'
            : `border-dashed border-muted cursor-pointer ${isDragging ? 'border-brand-500 bg-[rgba(99,102,241,0.05)]' : 'hover:border-brand-500 hover:bg-[rgba(99,102,241,0.05)]'}`
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!file ? handleBrowseClick : undefined}
      >
        {file ? (
          <div className="flex items-center gap-4">
            <div>
              {file.type.includes('pdf') ? <FileText className="w-10 h-10" /> : <Image className="w-10 h-10" />}
            </div>
            <div className="flex-1 text-left">
              <span className="block font-medium text-foreground break-all">{file.name}</span>
              <span className="block text-sm text-secondary-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
            </div>
            <button
              className="bg-transparent border-none text-secondary-foreground cursor-pointer p-2 rounded-full transition-colors hover:bg-[rgba(245,101,101,0.1)] hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); setFile(null); }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <FolderOpen className="w-10 h-10 mb-4" />
            <p className="text-base text-foreground mb-2">
              Drag and drop your resume here, or{' '}
              <span className="text-brand-500 cursor-pointer font-medium hover:underline">browse</span>
            </p>
            <p className="text-sm text-secondary-foreground">Supports PDF, JPG, PNG, WEBP (max 10MB)</p>
          </>
        )}
      </div>

      {isParsing && (
        <div className="my-8">
          <div className="h-2 bg-subtle rounded-full overflow-hidden">
            <div className="h-full transition-all duration-300 bg-brand-500" style={{ width: `${progress}%` }}></div>
          </div>
          <p className="text-center mt-3 text-secondary-foreground text-sm">
            {progress < 30 ? 'Uploading file...' : 'Parsing resume with AI...'}
          </p>
        </div>
      )}

      <div className="flex gap-4 justify-center mt-8">
        <button className="h-10 px-5 text-sm text-zinc-600 hover:text-foreground transition-colors font-medium bg-transparent border border-muted rounded-btn inline-flex items-center gap-2" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <button
          className="h-10 px-5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2"
          onClick={handleUploadAndParse}
          disabled={!file || isUploading}
        >
          {isUploading ? 'Processing...' : (
            <>
              <Upload className="w-4 h-4" />
              Upload & Parse
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default UploadResume;
